import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Grid,
  Chip,
  Link
} from '@mui/material';
import axios from 'axios';
import dayjs from 'dayjs';
import { formatDateTimeWithTimezone } from '../../utils/timezoneUtils';
import UpcomingMaintenanceItemSummary, {
  useResolvedUpcomingItem,
  hasUpcomingSource,
  hasAnySource,
  IntimationStoredSourceSummary
} from './UpcomingMaintenanceItemSummary';

const STATUS_COLORS = {
  PENDING: 'info',
  ACKNOWLEDGED: 'success',
  CLARIFICATION_REQUESTED: 'warning',
  COMPLETED: 'default',
  ARCHIVED: 'default'
};

/**
 * View a single intimation with linked work orders (clickable to open WO detail).
 */
const IntimationDetailDialog = ({ open, onClose, intimation, onNavigateToWorkOrder, displayTimezone, upcomingMaintenance = [] }) => {
  const [linkedWorkOrders, setLinkedWorkOrders] = useState([]);
  const [loadingWOs, setLoadingWOs] = useState(false);
  const { item: sourceUpcomingItem, loading: loadingSourceItem } = useResolvedUpcomingItem(
    intimation,
    upcomingMaintenance,
    open
  );

  useEffect(() => {
    if (!open || !intimation?._id) {
      setLinkedWorkOrders([]);
      return;
    }
    setLoadingWOs(true);
    axios
      .get(`/api/intimations/${intimation._id}/linked-work-orders`)
      .then((res) => setLinkedWorkOrders(res.data || []))
      .catch(() => setLinkedWorkOrders([]))
      .finally(() => setLoadingWOs(false));
  }, [open, intimation?._id]);

  if (!intimation) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Intimation: {intimation.title}</DialogTitle>
      <DialogContent>
        {hasUpcomingSource(intimation) && (
          loadingSourceItem ? (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Loading upcoming maintenance details…
            </Typography>
          ) : sourceUpcomingItem ? (
            <UpcomingMaintenanceItemSummary
              item={sourceUpcomingItem}
              displayTimezone={displayTimezone}
              title="Linked upcoming maintenance item"
            />
          ) : (
            <IntimationStoredSourceSummary intimation={intimation} />
          )
        )}
        {!hasUpcomingSource(intimation) && hasAnySource(intimation) && (
          <IntimationStoredSourceSummary intimation={intimation} />
        )}
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          <Grid item xs={12}>
            <Typography variant="caption" color="text.secondary">ID</Typography>
            <Typography variant="body2">{String(intimation._id).slice(-8)}</Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="caption" color="text.secondary">Status</Typography>
            <Box><Chip label={intimation.status} size="small" color={STATUS_COLORS[intimation.status] || 'default'} /></Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="caption" color="text.secondary">Aircraft</Typography>
            <Typography variant="body2">{intimation.aircraftId?.registration || '–'}</Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="caption" color="text.secondary">Created by</Typography>
            <Typography variant="body2">
              {intimation.createdBy ? `${intimation.createdBy.firstName || ''} ${intimation.createdBy.lastName || ''}`.trim() || '–' : '–'}
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="caption" color="text.secondary">Created</Typography>
            <Typography variant="body2">{intimation.createdAt ? (displayTimezone ? formatDateTimeWithTimezone(intimation.createdAt, null, displayTimezone) : dayjs(intimation.createdAt).format('DD MMM YYYY HH:mm')) : '–'}</Typography>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="caption" color="text.secondary">Included items</Typography>
            <Typography variant="body2">{intimation.includedItems || '–'}</Typography>
          </Grid>
          {intimation.excludedItems && (
            <Grid item xs={12}>
              <Typography variant="caption" color="text.secondary">Excluded items</Typography>
              <Typography variant="body2">{intimation.excludedItems}</Typography>
            </Grid>
          )}
          {intimation.description && (
            <Grid item xs={12}>
              <Typography variant="caption" color="text.secondary">Description</Typography>
              <Typography variant="body2">{intimation.description}</Typography>
            </Grid>
          )}
          {intimation.status === 'ACKNOWLEDGED' && (intimation.workOrderNumber || intimation.linkedWorkOrderId?.workOrderNumber) && (
            <Grid item xs={12}>
              <Typography variant="caption" color="text.secondary">Work order (legacy link)</Typography>
              <Typography variant="body2">{intimation.workOrderNumber || intimation.linkedWorkOrderId?.workOrderNumber}</Typography>
            </Grid>
          )}
          {intimation.status === 'COMPLETED' && intimation.completedAt && (
            <Grid item xs={12} md={6}>
              <Typography variant="caption" color="text.secondary">Completed</Typography>
              <Typography variant="body2">
                {displayTimezone ? formatDateTimeWithTimezone(intimation.completedAt, null, displayTimezone) : dayjs(intimation.completedAt).format('DD MMM YYYY HH:mm')}
              </Typography>
            </Grid>
          )}

          <Grid item xs={12}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>Linked work orders</Typography>
            {loadingWOs ? (
              <Typography variant="body2" color="text.secondary">Loading…</Typography>
            ) : linkedWorkOrders.length === 0 ? (
              <Typography variant="body2" color="text.secondary">None</Typography>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                {linkedWorkOrders.map((wo) => (
                  <Link
                    key={wo._id}
                    component="button"
                    variant="body2"
                    onClick={() => onNavigateToWorkOrder && onNavigateToWorkOrder(wo._id)}
                    sx={{ textAlign: 'left' }}
                  >
                    {wo.workOrderNumber || wo.title || wo._id} – {wo.title} ({wo.status})
                  </Link>
                ))}
              </Box>
            )}
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default IntimationDetailDialog;
